import { ComponentPortal } from '@angular/cdk/portal';
import { UntypedFormGroup } from '@angular/forms';
import { Sort } from '@angular/material/sort';
import { TableVirtualScrollDataSource } from 'ng-table-virtual-scroll';
import { Observable } from 'rxjs';
import { SummaryData } from 'src/app/modules/reports-builder/components/report-summary/report-summary.models';
import { BaseResponse, ListResponse } from '../../../core';

export interface ReportListViewDto {
	name?: string;
	email?: string;
	enrolment_date?: Date;
	started?: Date;
	completed?: Date;
	status?: string;
}

export enum ReportColumns {
	ALL,
	USER_NAME,
	EMAIL,
	ENROLMENT_DATE,
	STARTED,
	COMPLETED,
	STATUS
}

export enum ResponseMessageType {
	SUCCESS = 'succes',
	ERROR = 'error'
}

export interface ReportTableColumnsOptions {
	ref: string;
	cellDataKey: string;
	label?: string;
	sortDisabled?: boolean;
	sticky?: boolean;
	customTemplate?: ComponentPortal<any>;
	customTemplateAttached?: (CdkPortalOutletAttachedRef, any) => void;
	customHeaderTemplate?: ComponentPortal<any>;
	customHeaderTemplateAttached?: (CdkPortalOutletAttachedRef) => void;
	rowClass?: string;
	hidden?: boolean;
}

export interface MockDataType {
	position: number;
	name: string;
	weight: number;
	symbol: string;
}

export interface ReportTableOptions<T> {
	dataSource: Partial<ListResponse<T>> | Partial<BaseResponse<TableVirtualScrollDataSource<T>>>;
	columns: ReportTableColumnsOptions[];
	displayedColumns: string[];
	onSortChange: (sort: Sort) => void;
	stickyHeader?: boolean;
	virtualScroll?: Partial<VirtualScrollOptions>;
	tableClass: string;
	onlyHeader?: boolean;
}

export interface VirtualScrollOptions {
	itemSize: number;
	headerHeight: number;
	height: string;
	bufferMultiplier: number;
}

export const VIRTUAL_SCROLL_DEFAULT_OPTIONS: VirtualScrollOptions = {
	headerHeight: 56,
	height: '400px',
	itemSize: 48,
	bufferMultiplier: 0.7
};

export interface ReportsBuildersDto {
	date_range_preset?: DateRangePresets;
	date_range?: any;
	from_date?: any;
	to_date?: any;
	filters: ReportFiltersObject;
	cudFilters: PortalCudFilter[];
}

export interface PortalFilter {
	type: ReportFilterTypes;
	value: any | any[];
}

export interface PortalCudFilter {
	value: any;
	cud_definition_id: number;
}

export interface ColumnConfig {
	ref: string;
	cellDataKey: string;
	label: string;
	sortDisabled?: boolean;
	hidden?: boolean;
	customTemplate?: ComponentPortal<any>;
	customTemplateAttached?: (ref: any, rowData: any) => any;
}

export type Column = {
	default_selection: boolean;
	identifier: string;
	label_key: string;
	previousIndex?: number;
};

export interface ColumnObject {
	[key: string]: { Column };
}

export interface ColumnApiResponse {
	columns: {
		[key: string]: Column;
	};
}

export interface ReportData {
	availableColumns: Column[];
	date_range_preset?: DateRangePresets;
	filters: any[]; // TODO: change
	cudFilters: any[];
	initialColumns: Column[];
	initialExistingColumns?: Column[];
	reportColumns: Column[];
	schedule?: any;
}

export interface ReportTableData {
	enrollmentReportData: any[];
	reportFormData: ReportData;
	reportSummaryData?: SummaryData[];
}
export interface ReportApiData {
	rows: string[] | null;
	columns: string[];
}

export enum ReportFilterApiTypes {
	enrollment_period_date_range_preset = 'enrollment_period_date_range_preset',
	enrollment_period_from_date = 'enrollment_period_from_date',
	enrollment_period_to_date = 'enrollment_period_to_date',
	course = 'course_ids',
	enrollment_status = 'enrollment_statuses',
	user_status = 'user_statuses',
	user_type = 'user_types',
	group = 'group_ids',
	columns = 'columns',
	associations_membership_types = 'associations_membership_types',
	training = 'training_ids',
	session_status = 'session_statuses',
	user = 'user_ids',
	overdue = 'due_date_passed',
	session_attendance = 'session_detail_status',
	training_enrollment_statuses = 'training_enrollment_statuses',
	session_date_date_range_preset = 'session_date_date_range_preset',
	session_date_to_date = 'session_date_to_date',
	session_date_from_date = 'session_date_from_date',
	completion_period_date_range_preset = 'completion_period_date_range_preset',
	completion_period_from_date = 'completion_period_from_date',
	completion_period_to_date = 'completion_period_to_date',
	portal = 'portal_ids',
	multi_portal_course = 'course_ids',
	licensed = 'show_licensed_courses',
	multi_portal_user = 'user_ids',
	lp_enrollment_period_date_range_preset = 'lp_enrollment_period_date_range_preset',
	lp_enrollment_period_from_date = 'lp_enrollment_period_from_date',
	lp_enrollment_period_to_date = 'lp_enrollment_period_to_date',
	lp_completion_period_date_range_preset = 'lp_completion_period_date_range_preset',
	lp_completion_period_from_date = 'lp_completion_period_from_date',
	lp_completion_period_to_date = 'lp_completion_period_to_date',
	learning_path = 'learning_paths'
}
export enum ReportFilterTypes {
	COURSE_STATUS = 'course_status',
	USER_TYPE = 'user_type',
	USER_STATUS = 'user_status',
	GROUP = 'group',
	ENROLLMENT_STATUS = 'enrollment_status',
	COURSE = 'course',
	ASSOCIATIONS_MEMBERSHIP_TYPES = 'associations_membership_types',
	ENROLLMENT_PERIOD = 'enrollment_period',
	TRAINING = 'training',
	SESSION_STATUS = 'session_status',
	USER = 'user',
	OVERDUE = 'overdue',
	SESSION_ATTENDANCE = 'session_attendance',
	SESSION_DATE = 'session_date',
	TRAINING_ENROLLMENT_STATUS = 'training_enrollment_statuses',
	COMPLETION_PERIOD = 'completion_period',
	PORTAL = 'portal',
	MULTI_PORTAL_COURSE = 'multi_portal_course',
	MULTI_PORTAL_GROUP = 'multi_portal_group',
	MULTI_PORTAL_USER = 'multi_portal_user',
	MULTI_PORTAL_REPORT = 'multi_portal_report',
	LICENSED = 'licensed',
	LP_COMPLETION_PERIOD = 'lp_completion_period',
	LP_ENROLLMENT_PERIOD = 'lp_enrollment_period',
	LEARNING_PATH = 'learning_path',
	LP_ENROLLMENT__STATUS = 'lp_enrollment_status'
}

export enum DateRangePresets {
	ALL_TIME,
	CUSTOM,
	LAST_SEVEN_DAYS,
	LAST_MONTH,
	LAST_THREE_MONTHS,
	LAST_SIX_MONTHS,
	LAST_YEAR,
	LAST_FOURTEEN_DAYS
}

export enum ReportType {
	ENROLLMENTS_REPORT,
	ILT_REPORT
}

export enum Licensed {
	ALL = 1,
	LICENSED = 2,
	NOT_LICENSED = 3
}

export enum UserStatus {
	LOGIN_DISABLED = 0,
	LOGIN_ENABLED = 1
}

export enum ReportsBuilderErrorMessages {
	DATA_LIMIT_REACHED = 'data_limit_reached',
	TOO_MANY_REQUESTS = 'too_many_requests',
	GENERIC_ERROR = 'generic_error'
}

export enum OverdueStatus {
	YES = 1,
	NO = 0
}

export enum LegacyReportReportType {
	COURSE_SALES = 'course sales',
	COURSE_STATUS = 'course status',
	COURSE_HISTORY = 'training history',
	COURSE_MODULES = 'course modules',
	CERTS_AND_CREDITS = 'certs and credits',
	ILT_REPORT = 'ilt_report',
	GAMIFICATION = 'gamification',
	COURSE_EXAM = 'course exams',
	COURSE_SURVEY = 'course survey',
	SCORM = 'scorm interactions',
	TINCAN = 'tincan',
	ACTIVE_USERS = 'active users',
	EXTERNAL_TRAININGS = 'external trainings',
	LEARNING_PATHS = 'learning paths'
}

export enum ReportBuilderReportType {
	PROGRESS_REPORT = 'progress-report',
	ILT_REPORT = 'ilt-report',
	MULTI_PORTAL_REPORT = 'multi-portal-report',
	LEARNING_PATH_REPORT = 'learning-path-report'
}

export enum ReportBuilderColumnTypes {
	DATE_LAST_ACCESSED = 'date_last_accessed',
	DATE_COMPLETED = 'date_completed',
	CREATED_AT = 'created_at',
	DATE_STARTED = 'date_started',
	PERCENTAGE = 'percentage',
	PERCENTAGE_COMPLETE = 'percentage_complete',
	STATUS_ID = 'status_id',
	COURSE_ID = 'course_id',
	DUE_DATE_PASSED = 'due_date_passed',
	LOGIN_ENABLED = 'login_enabled',
	HAS_ATTEMPTS_REMAINING = 'has_attempts_remaining',
	NAME = 'name',
	EMAIL = 'email',
	GROUP = 'group_titles',
	MINUTES_ATTENDED = 'minutes_attended',
	TRAINING_ENROLLMENT_STATUS = 'training_enrollment_status',
	TRAINING_ENROLLMENT_STATE_STATUS = 'training_enrollment_state_status',
	SESSION_DETAILS_STATUS = 'session_detail_status',
	SESSION_DETAILS_STATUS_SET_BY = 'status_set_by_pm_id',
	SESSION_DETAILS_STATUS_SET_BY_ID = 'status_set_by_full_name_id',
	COURSE_NAME = 'course_name',
	TRAINING_TITLE = 'training_title',
	TRAINING_ID = 'training_id',
	SESSION_ID = 'session_id',
	LICENSED = 'licensed',
	IS_NEXT_COURSE = 'is_next_course',
	LP_ENROLLMENT_STATUS = 'learning_path_enrollment_status'
}

export enum SimpleListViewTableHeight {
	LARGE = '1000px',
	SMALL = '100px'
}

export interface ScheduleModel {
	frequency: number;
	weekday: number;
	day_of_the_month: number;
	id: number;
	portal_id: number;
}
export interface ScheduleData {
	scheduleForm: UntypedFormGroup;
	emailForm: UntypedFormGroup;
	scheduleModel: ScheduleModel;
	emailModel: any;
}

export interface FilterData {
	filterForm: UntypedFormGroup;
	filterModel: FilterModel;
}

export interface FilterModel {
	cudFilters: PortalCudFilter[];
	filters: any;
}

export interface ColumnData {
	availableColumns: Column[];
	initialColumns: Column[];
	reportColumns: Column[];
	originalAvailableColumns: Column[];
}

export enum ColumnDataProps {
	AVAILABLE_COLUMNS = 'availableColumns',
	INITIAL_COLUMNS = 'initialColumns',
	REPORT_COLUMNS = 'reportColumns',
	ORIGINAL_AVAILABLE_COLUMNS = 'originalAvailableColumns'
}

export const NumericStringReportColumns = [
	ReportBuilderColumnTypes.PERCENTAGE_COMPLETE,
	ReportBuilderColumnTypes.PERCENTAGE,
	ReportBuilderColumnTypes.MINUTES_ATTENDED,
	ReportBuilderColumnTypes.SESSION_ID,
	ReportBuilderColumnTypes.TRAINING_ID,
	ReportBuilderColumnTypes.COURSE_ID
];
export interface ReportSchedule {
	id?: number;
	schedule_id?: any;
	portal_id?: number;
	owner_pm_id?: number;
	reporting_template_id?: any;
	report_template_id?: any;
	frequency: number;
	day_of_the_month?: number;
	weekday?: number;
	timezone?: string;
}
export interface ReportTemplate {
	id?: ReportTemplateParamId;
	name?: string;
	report_type?: SaveReportTypes | any;
	report_type_id?: any;
	schedules?: ReportSchedule[] | any;
	owner_pm_id?: number;
	report_filters?: any;
	report_columns?: any;
	last_run?: string;
	created_at?: string;
	updated_at?: string;
	created_by?: string;
	run_count?: number;
}

export type ReportTemplateParamId = string | number;

export interface ReportPayload {
	columns: any;
	is_for_csv?: boolean;
	cuds?: string;
	enrollment_period_date_range_preset?: number;
	enrollment_period_from_date?: string;
	enrollment_period_to_date?: string;
	completion_period_date_range_preset?: number;
	completion_period_from_date?: string;
	completion_period_to_date?: string;
	session_date_date_range_preset?: number;
	session_date_from_date?: string;
	session_date_to_date?: string;
}

export type ReportFiltersObject = {
	[key in ReportFilterTypes]: number[] | string | any[] | any;
};

export type AsyncFiltersApiConfig = {
	[key in ReportFilterTypes]: Observable<BaseResponse<any>>;
};

export enum SaveReportTypes {
	'progress-report' = 1,
	'ilt-report' = 2,
	'multi-portal-report' = 3,
	'learning-path-report' = 4
}

export enum BooleanStrings {
	YES = 'yes_string',
	NO = 'no_string'
}

export type StringSortFn = (a: string, b: string) => number;

export interface DataWithReportTypeAndId extends ReportApiData {
	reportType?: ReportBuilderReportType;
	reportId?: ReportTemplateParamId;
}

export enum ScheduleFrequency {
	INACTIVE = 0,
	DAILY = 1,
	WEEKLY = 2,
	MONTHLY = 3
}

export enum FrequencyProps {
	'weekday' = 2,
	'day_of_the_month' = 3
}

export type SelectReportType = {
	title: string;
	description: string;
	actionTitle: string;
	icon: string;
	action: () => void;
	iconClass?: string;
	hidden?: boolean;
};

export type BasicSchedule = {
	title: string;
	tooltip: string;
	send_mondays: boolean;
	send_tuesdays: boolean;
	send_wednesdays: boolean;
	send_thursdays: boolean;
	send_fridays: boolean;
	send_saturdays: boolean;
	send_sundays: boolean;
	report_type: LegacyReportReportType;
	last_sent_at: string | null;
	id: number;
	frequency: ScheduleFrequency;
	day_of_month_to_send: number | null;
};
